import React, { useRef } from "react";
import { Dropdown } from "bootstrap";
import { ACTIVITY_ICON_OPTIONS, DEFAULT_ACTIVITY_ICON } from "./activityIcons";
import "./IconPicker.css";

interface IconPickerProps {
  value?: string | null;
  onChange: (icon: string) => void;
  disabled?: boolean;
  size?: number;
  toggleClassName?: string;
  /** Shows a focus-style ring around the toggle, e.g. while a sibling
   * field (like the type's name) is also being edited, so the two read
   * as one "you're editing this" unit. */
  highlighted?: boolean;
}

const IconPicker: React.FC<IconPickerProps> = ({
  value,
  onChange,
  disabled = false,
  size = 40,
  toggleClassName,
  highlighted = false,
}) => {
  const toggleRef = useRef<HTMLButtonElement>(null);
  const selected = ACTIVITY_ICON_OPTIONS.find((o) => o.value === value);
  const displayIcon = value || DEFAULT_ACTIVITY_ICON;

  const handleSelect = (iconValue: string) => {
    onChange(iconValue);
    // Close explicitly via Bootstrap's own API rather than relying on its
    // implicit auto-close-on-item-click, which doesn't reliably fire once
    // the selection also triggers a React re-render of this same button.
    const toggleEl = toggleRef.current;
    if (toggleEl) {
      Dropdown.getOrCreateInstance(toggleEl).hide();
    }
  };

  return (
    <div className="dropdown">
      <button
        ref={toggleRef}
        type="button"
        className={[
          toggleClassName || "icon-picker-toggle",
          highlighted ? "icon-picker-toggle--editing" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label="Choose an icon"
        disabled={disabled}
        style={toggleClassName ? undefined : { width: size, height: size }}
        title={selected ? selected.label : "Choose an icon"}
      >
        <i className={displayIcon} />
      </button>
      <div className="dropdown-menu icon-picker-menu">
        <div className="icon-picker-grid">
          {ACTIVITY_ICON_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`dropdown-item icon-picker-cell ${
                value === option.value ? "active" : ""
              }`}
              title={option.label}
              onClick={() => handleSelect(option.value)}
            >
              <i className={option.value} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IconPicker;
